import React from "react";
import { View, Text, StyleSheet, Platform, TouchableOpacity } from "react-native";
import FontFamily from "../../../../constants/fonts";
import { useTheme } from "../../../../theme/useTheme";

// Card separator component
export const CardSeparator = () => <View style={{ height: 12 }} />;

// Card component for iOS mobile employee display
interface EmployeeCardItemProps {
  item: any;
  columns: any[];
  onRowClick: (rowData: any) => void;
  onToggleNewHire?: (id: number, newValue: boolean) => void;
}

const EmployeeCardItem: React.FC<EmployeeCardItemProps> = ({ item, columns, onRowClick, onToggleNewHire }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark" && Platform.OS !== "web";

  // Get the status tag style based on status value
  const getStatusTagStyle = (status: string) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("active") && !statusLower.includes("inactive")) {
      return isDark ? styles.statusTagActiveDark : styles.statusTagActive;
    }
    if (statusLower.includes("inactive")) {
      return isDark ? styles.statusTagInactiveDark : styles.statusTagInactive;
    }
    if (statusLower.includes("terminated")) {
      return isDark ? styles.statusTagTerminatedDark : styles.statusTagTerminated;
    }
    return isDark ? styles.statusTagInactiveDark : styles.statusTagDefault;
  };

  // Get the status text color
  const getStatusTextColor = (status: string) => {
    if (isDark) return "#F2F2F2";
    return "#010101ff";
  };

  // Build primary display value (First Name + Last Name)
  const primaryValue = `${item.firstName || ""} ${item.lastName || ""}`.trim() || "N/A";

  // Status column
  const statusValue = item.status || "Active";

  // Define specific fields to show (matching Figma order)
  const displayFields = [
    "manager",
    "lastObservation", 
    "lastPulseCheck",
    "hireDate"
  ];

  // Get all columns for display (both main and remaining)
  const allDisplayColumns = columns.filter(col => 
    ![
      "firstName", 
      "lastName", 
      "status", 
      "actions", 
      "gpid", 
      "jobDescription", 
      "newHire",
      "graduationDate"
    ].includes(col.columnKey)
  );

  return (
    <View style={[styles.cardContainer, isDark && { backgroundColor: colors.background.surface }]}>
      {/* Header Row: Name + Status Tag (matching Figma Frame 427319497) */}
      <View style={styles.cardHeader}>
        <Text style={[styles.cardPrimaryText, isDark && { color: colors.brand.primary }]} numberOfLines={2}>
          {primaryValue}
        </Text>
        
        <View style={[styles.statusTag, getStatusTagStyle(statusValue)]}>
          <Text
            style={[
              styles.statusTagText,
              { color: getStatusTextColor(statusValue) },
            ]}
          >
            {statusValue}
          </Text>
        </View>
      </View>

      {/* New Hire Tag - shown only on mobile, toggleable by tapping */}
      <View style={styles.newHireTagContainer}>
        <TouchableOpacity
          onPress={() => {
            if (onToggleNewHire && item.id) {
              onToggleNewHire(item.id, !item.newHire);
            }
          }}
          activeOpacity={0.7}
          style={[
            styles.newHireTag,
            item.newHire
              ? (isDark ? styles.newHireTagActiveDark : styles.newHireTagActive)
              : (isDark ? styles.newHireTagInactiveDark : styles.newHireTagInactive),
          ]}
        >
          <Text style={[
            styles.newHireTagText,
            item.newHire
              ? styles.newHireTagTextActive
              : (isDark ? styles.newHireTagTextInactiveDark : styles.newHireTagTextInactive),
          ]}>
            {item.newHire ? "NEW HIRE" : "NOT NEW HIRE"}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Subheader: Job Description and GPID (matching Figma Frame 427319503) */}
      <View style={styles.cardSubheader}>
        {item.jobDescription && (
          <Text style={[styles.subheaderText, isDark && { color: colors.text.secondary }]}>{item.jobDescription}</Text>
        )}
        {item.gpid && (
          <Text style={[styles.subheaderText, isDark && { color: colors.text.secondary }]}>GPID: {item.gpid}</Text>
        )}
      </View>

      {/* Content: Label-Value Pairs (matching Figma Frame 427319501) */}
      <View style={styles.cardContent}>
        {/* All display columns - Label-Value pairs */}
        {allDisplayColumns.map((column, index) => {
          let value = column.renderCell
            ? column.renderCell(item)
            : item[column.columnKey];
          
          // Handle Date objects
          if (value instanceof Date) {
            value = value.toLocaleDateString();
          }

          // Handle React elements (like toggle switches)
          if (React.isValidElement(value)) {
            return (
              <View key={`${column.columnKey}-${index}`} style={styles.cardRow}>
                <Text style={[styles.cardRowLabel, isDark && { color: colors.text.secondary }]}>
                  {column.header.toUpperCase()}:
                </Text>
                <View style={{ flex: 1 }}>
                  {value}
                </View>
              </View>
            );
          }

          return (
            <View key={`${column.columnKey}-${index}`} style={styles.cardRow}>
              <Text style={[styles.cardRowLabel, isDark && { color: colors.text.secondary }]}>
                {column.header.toUpperCase()}:
              </Text>
              <Text style={[styles.cardRowValue, isDark && { color: colors.text.primary }]}>
                {typeof value === "string" || typeof value === "number" ? value : value || "-"}
              </Text>
            </View>
          );
        })}
      </View>

      {/* View Details Link - opens overlay when clicked */}
      {allDisplayColumns.length > 0 && (
        <View style={styles.viewDetailsContainer}>
          <TouchableOpacity
            onPress={(e) => {
              onRowClick(item);
            }}
            style={styles.viewDetailsButton}
            activeOpacity={0.7}
          >
            <Text style={[styles.viewDetailsText, isDark && { color: colors.brand.primary }]}>Edit Details</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Tap anywhere overlay for navigation */}
      <TouchableOpacity
        style={[
          styles.cardTapOverlay,
          {
            bottom: allDisplayColumns.length > 0 ? 40 : 0,
          },
        ]}
        onPress={() => onRowClick(item)}
        activeOpacity={0.1}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 20,
    marginBottom: 0,
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow: "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
      },
    }),
    position: "relative",
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  newHireTagContainer: {
    marginBottom: 8,
  },
  newHireTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    alignSelf: "flex-start",
  },
  newHireTagActive: {
    backgroundColor: "#106E98",
  },
  newHireTagActiveDark: {
    backgroundColor: "#0A4A66",
  },
  newHireTagInactive: {
    backgroundColor: "#E9ECEF",
    borderWidth: 1,
    borderColor: "#CED4DA",
  },
  newHireTagInactiveDark: {
    backgroundColor: "#2A2A2A",
    borderWidth: 1,
    borderColor: "#404040",
  },
  newHireTagText: {
    fontSize: 10,
    fontWeight: "700",
    lineHeight: 14,
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  newHireTagTextActive: {
    color: "#FFFFFF",
  },
  newHireTagTextInactive: {
    color: "#6C757D",
  },
  newHireTagTextInactiveDark: {
    color: "#B0B0B0",
  },
  cardPrimaryText: {
    fontSize: 16,
    fontWeight: "700",
    color: "#0061C7", // Figma color
    lineHeight: 24,
    textAlign: "left",
    fontFamily: FontFamily.regular,
    flex: 1,
    marginRight: 8,
  },
  cardSubheader: {
    marginBottom: 16,
    gap: 4,
  },
  subheaderText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#2D2D2D", // Figma color for job description and GPID
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  statusTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  statusTagText: {
    fontSize: 10,
    fontWeight: "400",
    textAlign: "center",
    lineHeight: 14,
    color: "#000000", // Figma default tag text color
    fontFamily: FontFamily.regular,
  },
  statusTagActive: {
    backgroundColor: "#98E492", // Figma color
  },
  statusTagActiveDark: {
    backgroundColor: "#1A4D1A",
  },
  statusTagInactive: {
    backgroundColor: "#6C757D",
  },
  statusTagInactiveDark: {
    backgroundColor: "#3A3A3A",
  },
  statusTagTerminated: {
    backgroundColor: "#C12C01",
  },
  statusTagTerminatedDark: {
    backgroundColor: "#6B1500",
  },
  statusTagDefault: {
    backgroundColor: "#6C757D",
  },
  cardContent: {
    marginBottom: 0,
    gap: 8, // 8px between fields as per Figma
  },
  cardRow: {
    marginBottom: 0,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 4, // 4px gap between label and value
  },
  cardRowText: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  cardRowLabel: {
    fontWeight: "700",
    fontSize: 14,
    color: "#4B4B4B", // Figma color for labels
    fontFamily: FontFamily.regular,
  },
  cardRowValue: {
    fontWeight: "400",
    fontSize: 14,
    color: "#4B4B4B", // Figma color for values
    fontFamily: FontFamily.regular,
    flex: 1,
  },
  viewDetailsContainer: {
    paddingVertical: 4,
    marginBottom: -2,
    zIndex: 10,
  },
  viewDetailsButton: {
    zIndex: 11,
  },
  viewDetailsText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
    fontFamily: FontFamily.regular,
  },
  cardTapOverlay: {
    position: "absolute",
    top: 90, // Adjusted for header + subheader height
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 1,
  },
});

export default EmployeeCardItem;
